/**
 * Période CRA alignée sur la fin opérationnelle mission (résiliation anticipée incluse).
 * Source partagée SPA (alias Vite) et API Next (available-periods, initialize, draft, submit).
 *
 * @see lib/missions/missionOperationalInProgressPeriod.ts (facturation / reporting)
 */

const OPERATIONAL_IN_PROGRESS_MISSION_STATUSES = new Set([
  'in-progress',
  'completed',
  'terminated',
])

function toYmdOnly(value) {
  if (value == null || value === '') return null
  const s = String(value).trim()
  if (s.length >= 10) return s.slice(0, 10)
  return s || null
}

/** Dernier jour opérationnel inclus (résiliation anticipée via termination_effective_date). */
export function getActivityReportMissionEndYmd(mission) {
  const status = mission?.status != null ? String(mission.status) : ''
  const endYmd = toYmdOnly(mission?.end_date)
  const effectiveYmd = toYmdOnly(mission?.termination_effective_date)

  if (status === 'terminated') {
    return effectiveYmd ?? endYmd
  }

  if (status === 'in-progress' && mission?.terminated_at) {
    return effectiveYmd ?? endYmd
  }

  return endYmd
}

function getMonthLastDayYmd(monthStartYmd, monthEndExclusiveYmd) {
  const d = new Date(`${monthEndExclusiveYmd}T12:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() - 1)
  return d.toISOString().slice(0, 10)
}

/** reportMonth = YYYY-MM */
export function reportMonthOverlapsMission(reportMonth, mission) {
  const trimmed = String(reportMonth ?? '').trim()
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return false

  const status = mission?.status != null ? String(mission.status) : ''
  if (!OPERATIONAL_IN_PROGRESS_MISSION_STATUSES.has(status)) return false

  const startYmd = toYmdOnly(mission?.start_date)
  if (!startYmd) return false

  const [y, m] = trimmed.split('-').map(Number)
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return false

  const monthStartYmd = `${y}-${String(m).padStart(2, '0')}-01`
  const monthEndExclusive =
    m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`
  const lastDayYmd = getMonthLastDayYmd(monthStartYmd, monthEndExclusive)

  if (startYmd > lastDayYmd) return false

  const operationalEndYmd = getActivityReportMissionEndYmd(mission)
  if (operationalEndYmd !== null && operationalEndYmd < monthStartYmd) return false

  return true
}

export const ACTIVITY_REPORT_PERIOD_OUT_OF_MISSION_MESSAGE =
  'The selected period is outside the mission operational dates'

function resolveTimesheetEntryType(entry) {
  return String(entry?.type || entry?.entryType || '').toLowerCase()
}

/** Lignes mensuelles / livrables : période portée par report_month, pas par le jour saisi. */
function isMonthScopedTimesheetEntry(entry) {
  const entryType = resolveTimesheetEntryType(entry)
  return entryType === 'monthly' || entryType === 'deliverable'
}

function normalizeMissionPeriodFields(mission) {
  if (!mission || typeof mission !== 'object') return mission
  return {
    ...mission,
    start_date: mission.start_date ?? mission.startDate,
    end_date: mission.end_date ?? mission.endDate,
    termination_effective_date:
      mission.termination_effective_date ?? mission.terminationEffectiveDate,
    terminated_at: mission.terminated_at ?? mission.terminatedAt,
  }
}

/**
 * Date représentative d'une ligne mensuelle / livrable dans le mois du CRA,
 * clampée entre le 1er du mois et les bornes opérationnelles mission.
 */
export function resolveMonthScopedTimesheetDateYmd(reportMonth, mission) {
  const trimmed = String(reportMonth ?? '').trim()
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return null

  const normalizedMission = normalizeMissionPeriodFields(mission)
  const [y, m] = trimmed.split('-').map(Number)
  const monthStartYmd = `${y}-${String(m).padStart(2, '0')}-01`
  const monthEndExclusive =
    m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`
  const monthEndYmd = getMonthLastDayYmd(monthStartYmd, monthEndExclusive)

  const startYmd = toYmdOnly(normalizedMission?.start_date) ?? monthStartYmd
  const operationalEndYmd = getActivityReportMissionEndYmd(normalizedMission) ?? monthEndYmd

  let dateYmd = monthStartYmd
  if (startYmd > dateYmd) dateYmd = startYmd
  if (operationalEndYmd && dateYmd > operationalEndYmd) dateYmd = operationalEndYmd
  return dateYmd
}

/** Valide report_month + dates des lignes timesheet (daily/hourly). */
export function validateActivityReportMissionPeriod({ reportMonth, mission, timesheets }) {
  if (reportMonth && !reportMonthOverlapsMission(reportMonth, mission)) {
    return {
      ok: false,
      message: ACTIVITY_REPORT_PERIOD_OUT_OF_MISSION_MESSAGE,
    }
  }

  const startYmd = toYmdOnly(mission?.start_date)
  const operationalEndYmd = getActivityReportMissionEndYmd(mission)
  const list = Array.isArray(timesheets) ? timesheets : []

  for (const entry of list) {
    if (isMonthScopedTimesheetEntry(entry)) continue

    const rawDate = entry?.date
    if (rawDate == null || rawDate === '') continue
    let dateKey = typeof rawDate === 'string' ? rawDate : String(rawDate)
    if (dateKey.includes('T')) dateKey = dateKey.split('T')[0]
    dateKey = dateKey.slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) continue

    if (startYmd && dateKey < startYmd) {
      return {
        ok: false,
        message: ACTIVITY_REPORT_PERIOD_OUT_OF_MISSION_MESSAGE,
      }
    }
    if (operationalEndYmd && dateKey > operationalEndYmd) {
      return {
        ok: false,
        message: ACTIVITY_REPORT_PERIOD_OUT_OF_MISSION_MESSAGE,
      }
    }
  }

  return { ok: true }
}

/** Freelancer CRA creation: hide periods older than this many months (inclusive window). */
export const FREELANCER_AVAILABLE_PERIOD_LOOKBACK_MONTHS = 6

export const ACTIVITY_REPORT_PERIOD_TOO_OLD_MESSAGE =
  'Activity report period is too far in the past'

function parseReportMonthYm(value) {
  const trimmed = String(value ?? '').trim()
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return null
  return trimmed
}

/** Oldest YYYY-MM a freelancer may pick when creating a new CRA (current month included in window). */
export function getFreelancerAvailablePeriodMinYm(referenceDate = new Date()) {
  const ref = referenceDate instanceof Date ? referenceDate : new Date(referenceDate)
  const anchor = new Date(ref.getFullYear(), ref.getMonth(), 1)
  anchor.setMonth(anchor.getMonth() - FREELANCER_AVAILABLE_PERIOD_LOOKBACK_MONTHS)
  const y = anchor.getFullYear()
  const m = anchor.getMonth() + 1
  return `${y}-${String(m).padStart(2, '0')}`
}

/** True when reportMonth is within the freelancer selectable window (todo periods). */
export function isFreelancerAvailableReportMonth(reportMonth, referenceDate = new Date()) {
  const ym = parseReportMonthYm(reportMonth)
  if (!ym) return false
  return ym >= getFreelancerAvailablePeriodMinYm(referenceDate)
}
